import { Suspense } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import { TOOLS_BY_SLUG, type ToolDef } from '../data';
import { getToolRunner } from '../runners/registry';
import { ToolErrorBoundary } from './tool-error-boundary';
import { useToolSurfaceConfig } from './tool-shell-context';
import { getToolIndexHref } from './tool-surface-config';

function NotFoundBlock({ slug }: { slug: string }) {
  const surfaceConfig = useToolSurfaceConfig();
  const indexHref = getToolIndexHref(surfaceConfig);

  return (
    <div className="rounded-xl border border-border bg-card p-8 text-center">
      <h2 className="text-xl font-bold text-foreground">Tool not found</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
        We don't have a tool at{' '}
        <code className="rounded bg-muted px-1 py-0.5 text-sm">
          {`${surfaceConfig.linkBase}/${slug}`}
        </code>
        {' '}yet.
      </p>
      <Link
        to={indexHref}
        className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:opacity-90"
      >
        Browse all tools
        <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

function ComingSoon({ tool }: { tool: ToolDef }) {
  const surfaceConfig = useToolSurfaceConfig();
  const indexHref = getToolIndexHref(surfaceConfig);

  return (
    <div className="rounded-xl border border-border bg-card p-8 text-center">
      <h2 className="mb-2 text-xl font-bold text-foreground">{tool.name} — coming soon</h2>
      <p className="text-muted-foreground">
        We're porting this tool. Check back shortly or browse{' '}
        <Link to={indexHref} className="font-medium text-primary hover:underline">
          all PDF tools
        </Link>
        .
      </p>
    </div>
  );
}

export function ToolRunnerHost({ slug }: { slug: string }) {
  const tool = TOOLS_BY_SLUG[slug];
  if (!tool) return <NotFoundBlock slug={slug} />;

  const Runner = getToolRunner(slug);

  return Runner ? (
    <ToolErrorBoundary toolName={tool.name} toolSlug={tool.slug}>
      <Suspense
        fallback={
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        }
      >
        <Runner slug={tool.slug} />
      </Suspense>
    </ToolErrorBoundary>
  ) : (
    <ComingSoon tool={tool} />
  );
}
